/**
 * @file 积分不足弹窗
 */

import {useCallback, useMemo} from 'react';
import {useModalVariant} from './useModalVariant';
import {VARIANT_CONFIG} from './constants';
import type {VariantButtonConfig} from './constants';
import styles from './index.module.less';
import Modal from '@/components/common/Modal';
import Button from '@/components/common/Button';
import Icon from '@/components/common/Icon';
import {useModalStore} from '@/store/useModalStore';
import {openMembershipModal, openPointsRechargeModal} from '@/commerce/modals/openModal';
import {useMemberLogExt, useMemberExtShowLog} from '@/hooks/memberLogExt';
import {PointsRechargeModalSource} from '@/commerce/modals/PointsRechargeModal/types';
import {useTracking} from '@/services/tracking/context';
import {TrackEvents} from '@/services/tracking/constant';
import {MembershipModalSource} from '@/commerce/modals/MembershipModal/types';
import {useRoleCodes} from '@/hooks/useRoleCodes';
import {useCanvasSessionMeta} from '@/store/canvas/useCanvasSessionStore';
import type {LogExt} from '@/services/tracking';

const ACTION_LOG_VALUE: Partial<Record<VariantButtonConfig['action'], string>> = {
    membership: TrackEvents.POPUP_OPEN_MEMBERSHIP,
    points: TrackEvents.POPUP_CREDIT_RECHARGE,
};

interface ActionButtonProps {
    config: VariantButtonConfig;
    onAction: (action: string) => void;
}

const ActionButton: React.FC<ActionButtonProps> = ({config: btn, onAction}) => {
    const handleClick = useCallback(() => onAction(btn.action), [btn.action, onAction]);
    return (
        <Button type={btn.type} onClick={handleClick}>
            {btn.label}
        </Button>
    );
};

const PointsInsufficientModal: React.FC = () => {
    const open = useModalStore((s) => s.pointsInsufficientModalOpen);
    const sceneKey = useModalStore((s) => s.pointsInsufficientSceneKey);
    const close = useModalStore((s) => s.closePointsInsufficientModal);
    const variant = useModalVariant();
    const config = VARIANT_CONFIG[variant];
    const {sendClickLog} = useTracking();
    const memberLogExt = useMemberLogExt();
    const roleCodes = useRoleCodes();
    const canvasType = useCanvasSessionMeta()?.canvasType ?? 0;

    const trackExt = useMemo<LogExt>(
        () => ({
            /* eslint-disable camelcase */
            scene_key: sceneKey ?? '',
            pop_type: config.popType,
            canvas_type: canvasType,
            user_role: roleCodes,
            /* eslint-enable camelcase */
        }),
        [sceneKey, config.popType, canvasType, roleCodes],
    );

    useMemberExtShowLog(
        open &&
            !!sceneKey && {
                page: TrackEvents.PAGE_INSUFFICIENT_CREDITS_POP,
                value: TrackEvents.INSUFFICIENT_CREDITS_POP,
                ext: trackExt,
            },
    );

    const handleAction = useCallback(
        (action: string) => {
            const logValue = ACTION_LOG_VALUE[action as VariantButtonConfig['action']];
            if (logValue && sceneKey) {
                sendClickLog({
                    page: TrackEvents.PAGE_INSUFFICIENT_CREDITS_POP,
                    value: logValue,
                    ext: {...trackExt, ...memberLogExt},
                });
            }

            close();
            switch (action) {
                case 'membership':
                    openMembershipModal(
                        MembershipModalSource.INSUFFICIENT_POINTS_REMINDER_POPUP_MEMBERSHIP_BTN,
                    );
                    break;
                case 'points':
                    openPointsRechargeModal(
                        PointsRechargeModalSource.INSUFFICIENT_POINTS_REMINDER_POPUP_RECHARGE_POINTS_BTN,
                    );
                    break;
                case 'close':
                default:
                    break;
            }
        },
        [close, sceneKey, sendClickLog, trackExt, memberLogExt],
    );

    const title = (
        <span className={styles.title}>
            <Icon name="AijBasicTishiblue" size={16} />
            {config.title}
        </span>
    );

    return (
        <Modal
            className={styles.modal}
            open={open}
            onCancel={close}
            size="sm"
            title={title}
            footer={null}
            centered
            destroyOnHidden
        >
            <p className={styles.description}>{config.description}</p>
            <div className={styles.footer}>
                {config.buttons.map((btn) => (
                    <ActionButton key={btn.key} config={btn} onAction={handleAction} />
                ))}
            </div>
        </Modal>
    );
};

export default PointsInsufficientModal;
